import React, { useState } from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import { Chip } from '@material-ui/core'

import { useQueryPaginator } from 'src/components/Table'
import { AddAttachmentsDialog } from 'src/components/Dialog/AddAttachmentsDialog'
import { EventListTable } from 'src/components/Table/list/Event'
import { ParcelShipmentBarcodedUnitListTable } from 'src/components/Table/list/ParcelShipmentBarcodedUnit'
import { ParcelShipmentUnitListTable } from 'src/components/Table/list/ParcelShipmentUnit'
import { ParcelShipmentDetailTable } from 'src/components/Table/detail/ParcelShipment'
import { GenericPreview, PaginatedFilePreviewGallery } from 'src/components/FilePreview'
import { ParcelShipmentDialog } from 'src/modules/supply_chain/components/parcel/ShipmentDialog'
import { Workflow } from 'src/modules/supply_chain/components/Workflow'

import { skuit } from 'src/services/skuit'
import { ParcelShipment, Tag } from 'src/api/models'
import { useQuery } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathParams {
  guid: string
}

export const ShipmentPage = () => {
  const { guid } = useParams<PathParams>()
  const [addAttachmentDialogOpen, setAddAttachmentDialogOpen] = useState(false)

  const shipment = useQuery<ParcelShipment>(['shipment-details', guid], () =>
    skuit.sc.parcel.shipments.retrieve(guid)
  )

  const attachmentsPaginator = useQueryPaginator(
    'attachment-list',
    () => skuit.sc.parcel.shipments.attachments.list(guid),
    {
      params: { page_size: 4, shipments: guid },
    }
  )
  const unitsPaginator = useQueryPaginator(
    'shipment-unit-list',
    skuit.sc.parcel.shipments.units.list,
    { params: { page_size: 5, shipments: guid } }
  )
  const barcodedPaginator = useQueryPaginator(
    'shipment-barcoded-unit-list',
    skuit.sc.parcel.shipments.barcodedUnits.list,
    { params: { page_size: 5, shipments: guid } }
  )
  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  const refresh = () => {
    shipment.refetch()
    attachmentsPaginator.refresh()
    unitsPaginator.refresh()
    barcodedPaginator.refresh()
    eventsPaginator.refresh()
  }

  return (
    <DetailPage
      obj={shipment}
      title="Parcel Shipment"
      refresh={refresh}
      breadcrumbs={[
        { title: 'Dashboard', url: '/' },
        { title: 'Parcel Shipments', url: '/parcel/shipments' },
      ]}
      detailComponent={<ParcelShipmentDetailTable obj={shipment} refresh={refresh} />}
      secondaryComponent={
        <Workflow
          title="Shipment Workflow"
          loading={shipment.isFetching}
          workflow={shipment.data?.workflow || []}
          status={shipment.data?.workflowStatus || ''}
          actions={shipment.data?.getWorkflowActions(() => shipment.refetch()) || {}}
        />
      }
      createDialog={{ title: 'New Parcel Shipment', Dialog: ParcelShipmentDialog }}
    >
      {addAttachmentDialogOpen && (
        <AddAttachmentsDialog
          onSuccess={refresh}
          addAttachment={(config) =>
            skuit.sc.parcel.shipments.attachments.create(guid, config?.data, config)
          }
          open={addAttachmentDialogOpen}
          handleClose={() => {
            setAddAttachmentDialogOpen(false)
          }}
        />
      )}
      <PaginatedFilePreviewGallery
        title="Attachments"
        paginator={attachmentsPaginator}
        getIdent={(item) => item.guid}
        onAdd={() => {
          setAddAttachmentDialogOpen(true)
        }}
        renderItem={(item) => {
          return (
            <>
              <GenericPreview url={item.file} />
              <div>
                {item.tags.map((tag: Tag) => (
                  <Chip key={tag.slug} label={tag.name} />
                ))}
              </div>
            </>
          )
        }}
      />
      <ParcelShipmentUnitListTable
        paginator={unitsPaginator}
        title="Units"
        fields={['productName', 'product', 'sku', 'batch', 'numUnits']}
      />
      <ParcelShipmentBarcodedUnitListTable
        paginator={barcodedPaginator}
        title="Units (Barcoded)"
        fields={['unit', 'product']}
      />
      <EventListTable
        paginator={eventsPaginator}
        title="Parcel Shipment Events"
        fields={['type', 'data', 'by', 'on']}
      />
    </DetailPage>
  )
}
